import React from 'react';
import { Box, Typography, Card, CardContent, CardActionArea, Grid, Container, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@frontend/components/Layout';
import { useTranslation } from '@frontend/i18n';
import ChatIcon from '@mui/icons-material/Chat';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import VideocamIcon from '@mui/icons-material/Videocam';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DatasetIcon from '@mui/icons-material/Dataset';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import PersonIcon from '@mui/icons-material/Person';

interface CategoryCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  modelCount: number;
  path: string;
  color: string;
}

function CategoryCard({ title, description, icon, modelCount, path, color }: CategoryCardProps): JSX.Element {
  const navigate = useNavigate();

  return (
    <Card
      sx={{
        height: '100%',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
    >
      <CardActionArea onClick={() => navigate(path)} sx={{ height: '100%', p: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${color}15`,
              color: color,
              mb: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
            {description}
          </Typography>
          <Chip label={`${modelCount} models`} size="small" sx={{ backgroundColor: 'background.default' }} />
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function Home(): JSX.Element {
  const t = useTranslation();

  const categories = [
    {
      title: t.home.categories.workspace.title,
      description: t.home.categories.workspace.description,
      icon: <WorkspacesIcon sx={{ fontSize: 32 }} />,
      modelCount: 2,
      path: '/workspace',
      color: '#06b6d4',
    },
    {
      title: t.home.categories.chat.title,
      description: t.home.categories.chat.description,
      icon: <ChatIcon sx={{ fontSize: 32 }} />,
      modelCount: 15,
      path: '/chat',
      color: '#3b82f6',
    },
    {
      title: t.home.categories.image.title,
      description: t.home.categories.image.description,
      icon: <ImageIcon sx={{ fontSize: 32 }} />,
      modelCount: 12,
      path: '/playground/image',
      color: '#8b5cf6',
    },
    {
      title: t.home.categories.audio.title,
      description: t.home.categories.audio.description,
      icon: <AudiotrackIcon sx={{ fontSize: 32 }} />,
      modelCount: 8,
      path: '/playground/audio',
      color: '#ec4899',
    },
    {
      title: t.home.categories.video.title,
      description: t.home.categories.video.description,
      icon: <VideocamIcon sx={{ fontSize: 32 }} />,
      modelCount: 6,
      path: '/playground/video',
      color: '#f59e0b',
    },
    {
      title: t.home.categories.virtualInfluencer.title,
      description: t.home.categories.virtualInfluencer.description,
      icon: <PersonIcon sx={{ fontSize: 32 }} />,
      modelCount: 4,
      path: '/virtual-influencer',
      color: '#ef4444',
    },
    {
      title: t.home.categories.workflows.title,
      description: t.home.categories.workflows.description,
      icon: <AccountTreeIcon sx={{ fontSize: 32 }} />,
      modelCount: 5,
      path: '/playground/workflows',
      color: '#10b981',
    },
    {
      title: t.home.categories.data.title,
      description: t.home.categories.data.description,
      icon: <DatasetIcon sx={{ fontSize: 32 }} />,
      modelCount: 4,
      path: '/playground/data',
      color: '#6366f1',
    },
  ];

  return (
    <Layout title={t.home.title}>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            {t.home.tryAiModels}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, maxWidth: 600, mx: 'auto' }}>
            {t.home.exploreAiModels}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {categories.map((category) => (
            <Grid item xs={12} sm={6} md={4} key={category.path}>
              <CategoryCard {...category} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Layout>
  );
}
